import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

export default function PrivacyPolicy() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Minimalist%20abstract%20light%20background%20with%20soft%20warm%20toned%20paper%20texture%20in%20cream%20and%20light%20beige%2C%20gentle%20natural%20light%20from%20top%20left%2C%20clean%20empty%20center%20area%2C%20subtle%20grain%20texture%2C%20artistic%20minimal%20composition%2C%20editorial%20style%20with%20quiet%20elegance&width=1600&height=600&seq=privacy-hero-2026&orientation=landscape"
            alt="プライバシーポリシー"
            title="プライバシーポリシー"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 40%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/35"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span
              className="text-background-50/80 text-xs tracking-[0.4em] mb-6"
              style={{ fontFamily: "var(--font-label)" }}
            >
              PRIVACY POLICY
            </span>
            <h1
              className="text-background-50 text-3xl md:text-5xl leading-[1.05] max-w-4xl whitespace-nowrap"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              プライバシーポリシー
            </h1>
          </div>
        </section>

        {/* Policy Content */}
        <section className="w-full px-6 md:px-10 max-w-[1600px] mx-auto py-16 md:py-24">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm text-foreground-600 leading-relaxed mb-12">
              ハヤブサワークス（以下「当方」といいます）は、お客様（以下「ユーザー」といいます）の個人情報の保護を重要視し、以下のプライバシーポリシー（以下「本ポリシー」といいます）に基づき、個人情報の適切な取扱いと保護に努めます。
            </p>

            {/* Section 1 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第1条（個人情報の定義）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本ポリシーにおいて「個人情報」とは、個人情報の保護に関する法律（以下「個人情報保護法」といいます）に定義される「個人情報」を指します。具体的には、氏名、住所、電話番号、メールアドレス、その他記述等により特定の個人を識別することができる情報をいいます。
              </p>
            </div>

            {/* Section 2 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第2条（収集する情報）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                当方は、以下の目的のため、ユーザーから以下の情報を収集する場合があります。
              </p>
              <ul className="space-y-2.5 text-sm text-foreground-600 leading-relaxed">
                {[
                  "お問い合わせフォームを通じてご提供いただく情報（氏名、メールアドレス、お問い合わせ内容）",
                  "当方ウェブサイト（以下「本サイト」といいます）のアクセス時に自動的に収集される情報（IPアドレス、ブラウザの種類、アクセス日時、参照URL等のアクセスログ）",
                  "Cookieや類似技術を用いて収集する情報",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-primary-500 text-background-50 text-[10px]">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 3 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第3条（利用目的）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                当方は、収集した個人情報を以下の目的のために利用します。法令に基づく場合を除き、これらの目的を超えた利用は行いません。
              </p>
              <ul className="space-y-2.5 text-sm text-foreground-600 leading-relaxed">
                {[
                  "ユーザーからのお問い合わせに対応するため",
                  "本サイトの運営・改善およびサービス品質の向上のため",
                  "本サイトの利用状況の分析および統計情報の作成のため",
                  "当方の商品・サービスに関する情報提供（ユーザーが希望された場合に限ります）",
                  "不正行為の防止およびセキュリティの確保のため",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-accent-500 text-background-50 text-[10px]">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 4 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第4条（第三者提供）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                当方は、以下のいずれかに該当する場合を除き、ユーザーの個人情報を第三者に提供することはありません。
              </p>
              <ul className="space-y-2.5 mt-4 text-sm text-foreground-600 leading-relaxed">
                {[
                  "ユーザー本人の同意がある場合",
                  "法令に基づく場合",
                  "人の生命、身体または財産の保護のために必要がある場合であって、ユーザーの同意を得ることが困難であるとき",
                  "公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、ユーザーの同意を得ることが困難であるとき",
                  "国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、ユーザーの同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-secondary-500 text-background-50 text-[10px]">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 5 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第5条（安全管理措置）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                当方は、ユーザーの個人情報を正確かつ最新の状態に保つとともに、不正アクセス、改ざん、漏洩、紛失および損壊を防止するため、組織的・人的・物理的・技術的な安全管理措置を適切に講じ、必要に応じて是正を行います。ただし、インターネットを介した情報通信の性質上、完全な安全性を保証するものではありません。
              </p>
            </div>

            {/* Section 6 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第6条（Cookieおよびアクセス解析ツールについて）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                本サイトでは、利便性向上およびアクセス解析のためにCookie（クッキー）および類似の技術を使用しています。Cookieとは、ウェブサイトがユーザーのブラウザに送信する小さなデータファイルであり、ユーザーのブラウザに保存されます。
              </p>
              <ul className="space-y-2.5 text-sm text-foreground-600 leading-relaxed">
                {[
                  "ユーザーはブラウザの設定によりCookieの受け入れを拒否することができますが、その場合、本サイトの一部機能が正しく動作しなくなる可能性があります。",
                  "本サイトでは、Google Analytics等のアクセス解析ツールを利用する場合があります。これらのツールが収集する情報は匿名であり、個人を特定するものではありません。",
                  "Google Analyticsのデータ収集・処理の仕組みについては、Google社のプライバシーポリシーをご確認ください。",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-primary-500 text-background-50 text-[10px]">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 7 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第7条（外部リンクについて）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本サイトには、外部サイトへのリンクが含まれている場合があります。当方は、リンク先のウェブサイトにおける個人情報の取扱いについて責任を負いません。リンク先のプライバシーポリシーを各自でご確認いただきますようお願いします。
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第8条（お問い合わせ窓口）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                本ポリシーに関するお問い合わせ、または個人情報の開示・訂正・利用停止等をご希望される場合は、以下までご連絡ください。
              </p>
              <div className="bg-background-50 rounded-lg border border-background-200/60 p-6 space-y-3 text-sm text-foreground-600 leading-relaxed">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5 text-primary-500">
                    <i className="ri-building-line"></i>
                  </span>
                  <span>
                    <strong className="text-foreground-950">運営者：</strong>ハヤブサワークス
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5 text-primary-500">
                    <i className="ri-map-pin-line"></i>
                  </span>
                  <span>
                    <strong className="text-foreground-950">所在地：</strong>島根県松江市
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5 text-primary-500">
                    <i className="ri-mail-line"></i>
                  </span>
                  <span>
                    <strong className="text-foreground-950">メールアドレス：</strong>
                    <a
                      href="mailto:hayabusaworks2024@gmail.com"
                      className="text-primary-500 hover:text-primary-600 transition cursor-pointer"
                    >
                      hayabusaworks2024@gmail.com
                    </a>
                  </span>
                </div>
              </div>
            </div>

            {/* Section 9 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第9条（ポリシーの改定）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                当方は、法令の改正や事業上の必要性に応じて、本ポリシーを随時改定することがあります。重要な変更がある場合には、本サイト上での告知その他適切な方法によりユーザーにお知らせします。改定後の本ポリシーは、本サイトに掲載された時点で効力を生じるものとします。
              </p>
            </div>

            {/* Section 10 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第10条（免責事項）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本サイトに掲載された内容によって生じた損害等の一切の責任を負いかねますのでご了承ください。また、本サイトからリンクやバナーなどによって他のサイトに移動された場合、移動先サイトで提供される情報、サービス等について一切の責任を負いません。
              </p>
            </div>

            {/* Section 11 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第11条（著作権・肖像権）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本サイトのコンテンツ（文章、画像、動画等）の著作権・肖像権は当サイトに帰属します。無断転載することを禁止します。
              </p>
            </div>

            {/* Enactment Date */}
            <div className="pt-6 border-t border-background-200/60 text-right">
              <p className="text-xs text-foreground-600">
                制定日：2026年7月14日
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}